import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { IServiceOrdersReportFilters } from "./Filters";
import { useLanguageContext } from "../../../../../contexts/LanguageContext";
import { IDefaultStatusData, ServicePriorityData, ServicePropertyData, ServiceStatusData } from "../../../../../data/Enums";
import { useEffect, useMemo } from "react";
import FormHelper from "../../../../../helpers/FormHelper";
import { HomeRepairService } from "@mui/icons-material";
import DefaultConstants from "../../../../../data/Constants";
import useState from "../../../../../hooks/useState";
import ServiceOrderService from "../../../../../services/ServiceOrderService";
import IServiceOrder from "../../../../../interfaces/IServiceOrder";
import DateTimeHelper from "../../../../../helpers/DateTimeHelper";


interface IStateValues {
    serviceOrders: IServiceOrder[];
}

const ServiceOrdersContent: React.FunctionComponent<IServiceOrdersReportFilters> = (filters) => {
    const { translate } = useLanguageContext();

    const state = useState<IStateValues>({
        serviceOrders: []
    });


    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'enterprise') {
                key = 'enterpriseId';
                value = value.id;
            } else if (key === 'status' || key === 'priority') {
                value = value.join(',');
            } else if (key === 'department' || key === 'responsible' || key === 'room') {
                value = value.map((r: any) => r.id).join(',');
            } else if (key === 'startDate' || key === 'estimatedCompletionDate') {
                value = value !== null && value !== undefined ? DateTimeHelper.Format(value) : '';
            }

            formData = FormHelper.Add(formData, key, value);
        });

        ServiceOrderService.List(formData).then(resp => {
            state.update('serviceOrders', resp.data.serviceOrders as IServiceOrder[]);
        }).catch(err => {
            state.update('serviceOrders', []);
        })
        // eslint-disable-next-line
    }, []);


    const renderFilterFieldset = useMemo(() => {
        return <TableCell className='reportpreview-paper-content-fieldset'>
                <Typography variant='caption' className='reportpreview-paper-content-fieldset-label'>
                    {translate('filters')}
                </Typography>

                <Grid container>
                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('serviceStatus')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.status.length === 0 ? (
                                    translate('all')
                                ) : (
                                    Object.values(ServiceStatusData)
                                        .filter(s => filters.status.includes(s.key))
                                        .map(s => translate(s.name))
                                        .join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('priority')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.priority.length === 0 ? (
                                    translate('all')
                                ) : (
                                    Object.values(ServicePriorityData)
                                        .filter(s => filters.priority.includes(s.key))
                                        .map(s => translate(s.name))
                                        .join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('room')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.room.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.room.map(r => r.description).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('department')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.department.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.department.map(d => d.name).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('responsible')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.responsible.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.responsible.map(r => r.fullName).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('startDate')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.startDate !== null && filters.startDate !== undefined ? (
                                    `${filters.startDateOperator} ${DateTimeHelper.Format(filters.startDate)}` 
                                ) : ('-')
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('estimatedCompletionDate')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.estimatedCompletionDate !== null && filters.estimatedCompletionDate !== undefined ? (
                                    `${filters.estimatedCompletionDateOperator} ${DateTimeHelper.Format(filters.estimatedCompletionDate)}` 
                                ) : ('-')
                            }
                        </Typography>
                    </Grid>
                </Grid>
            </TableCell>;
    }, [filters, translate]);


    const data = useMemo(() => {
        return state.values.serviceOrders;
        // eslint-disable-next-line
    }, [state.values.serviceOrders, filters]);
    
    
    return <>
        <TableRow>
            {renderFilterFieldset}
        </TableRow>

        <TableRow>
            <TableCell className='border-none' padding='none'>
                {
                    data.length > 0 ? (
                        <>
                            <Table className='data-table' sx={{ marginY: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('status')}</TableCell>
                                        <TableCell>{translate('serviceOrder')}</TableCell>
                                        <TableCell>{translate('priority')}</TableCell>
                                        <TableCell>{translate('createdAt')}</TableCell>
                                        <TableCell>{translate('room')}</TableCell>
                                        <TableCell>{translate('department')}</TableCell>
                                        <TableCell>{translate('responsible')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        data.map(row => {
                                            const status = Object.values(ServiceStatusData).find(s => s.key === +(row.statusId ?? 0)) as IDefaultStatusData;
                                            const priority = Object.values(ServicePriorityData).find(s => s.key === +(row.priorityId ?? 0)) as IDefaultStatusData;

                                            return <TableRow key={row.id}>
                                                <TableCell>
                                                    <Typography className='text-title' sx={{ color: `${status.color} !important` }}>
                                                        <status.Icon />
                                                        {translate(status.name)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> # {row.id} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title' sx={{ color: `${priority.color} !important` }}>
                                                        <priority.Icon />
                                                        {translate(priority.name)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {DateTimeHelper.Format(row.createdAt, 'DD/MM/YYYY')} </Typography>
                                                    <Typography className='text-subtitle'> {DateTimeHelper.Format(row.createdAt, 'HH:mm:ss')} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.room?.description ?? '-'} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.department?.name ?? '-'} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.responsible?.login ?? '-'} </Typography>
                                                    <Typography className='text-subtitle'> {row.responsible?.fullName} </Typography>
                                                </TableCell>

                                                <TableCell className='display-inlineflex'>
                                                    {
                                                        Object.values(ServicePropertyData).map(s => {
                                                            const key = (s.partnerId ?? '') as keyof(IServiceOrder);

                                                            if (row[key] !== undefined) {
                                                                let prefix = s.type === 'array' ? (row[key] as any[]).length : undefined;

                                                                return <Box className='verticalstatus-box width-auto'>
                                                                    <s.Icon />
                                                                    <Typography>{prefix} {translate(s.name).toLowerCase()}</Typography>
                                                                </Box>;
                                                            }

                                                            return null;
                                                        })
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </>
                    ) : (
                        <Box className='reportpreview-paper-content-notfound' marginBottom={DefaultConstants.gridRowSpacing}>
                            <Typography variant='body1'>{translate('_serviceOrdersEmptyList')}</Typography>
                        </Box>
                    )
                }

                {/* Summary */}
                <Table className='page-break show-only-in-print'>
                    {renderFilterFieldset}
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('summary')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        <TableRow>
                            <TableCell className='color-primary display-flex alignitems-center fontweight-600 gap-10'>
                                <HomeRepairService className='fontsize-20' />
                                {translate('serviceOrders')}
                            </TableCell>
                            <TableCell className='textalign-right fontweight-600'>{data.length}</TableCell>
                            <TableCell className='textalign-right fontweight-600' width={80}>100.0 %</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('serviceStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(ServiceStatusData).map(s => {
                                const value = data.filter(r => +(r.statusId ?? 0) === s.key).length;
                                const percentage = data.length === 0 ? 0 : value / data.length * 100;

                                return <TableRow>
                                    <TableCell 
                                        className='display-flex alignitems-center fontweight-600 gap-10'
                                        sx={{
                                            color: `${s.color} !important`
                                        }}
                                    >
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>;
                            })
                        }
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('priority')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(ServicePriorityData).map(s => {
                                const value = data.filter(r => +(r.priorityId ?? 0) === s.key).length;
                                const percentage = data.length === 0 ? 0 : value / data.length * 100;

                                return <TableRow>
                                    <TableCell 
                                        className='display-flex alignitems-center fontweight-600 gap-10'
                                        sx={{
                                            color: `${s.color} !important`
                                        }}
                                    >
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>;
                            })
                        }
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('properties')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(ServicePropertyData).filter(s => s.partnerId).map(s => {
                                const key = (s.partnerId ?? '') as keyof(IServiceOrder);

                                let value = 0;
                                if (s.type === 'array') {
                                    value = data
                                        .filter(r => r[key] !== undefined)
                                        .reduce((acc, curr) => {
                                            return acc + (curr[key] as any[]).length;
                                        }, 0);
                                }
                                
                                let percentage = 100;

                                return <TableRow>
                                    <TableCell className='display-flex alignitems-center fontweight-600 gap-10 color-primary'>
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>;
                            })
                        }
                    </TableBody>
                </Table>
            </TableCell>
        </TableRow>
    </>;
}

export default ServiceOrdersContent;